import { observer } from "mobx-react";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { BasePage } from "@/components/BasePage";
import LoadFailure from "@/components/common/LoadFailure";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { useResultsDashboard } from "@/hooks/useResultsDashboard";
import { useRootStore } from "@/state/RootStore";
import type { LoadStatus } from "@/state/loadStatus";
import { ResultsDashboard } from "./ResultsDashboard";
import { ResultsEmptyState } from "./ResultsPrimitives";
import { useTranslation } from "react-i18next";

const ExamResults = observer(() => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const store = useRootStore();
  const { model } = useResultsDashboard();
  const needsToCreateProfile = !store.mainAppStore.isProfileComplete;
  const testsStatus = useMemo<LoadStatus>(() => {
    const tests = store.testStore.testsStatus;
    const retired = store.questionsStore.deletedQuestionsStatus;
    if (tests === "error" || retired === "error") return "error";
    if (tests === "loaded" && retired === "loaded") return "loaded";
    return "loading";
  }, [store.questionsStore.deletedQuestionsStatus, store.testStore.testsStatus]);

  const retry = useCallback(() => {
    store.testStore.loadTests();
    if (store.questionsStore.deletedQuestionsStatus === "error") {
      store.questionsStore.loadQuestions();
    }
  }, [store]);

  if (testsStatus === "error") {
    return (
      <BasePage activeItem="results" contentClassName="pt-0 md:pt-0">
        <LoadFailure onRetry={retry} />
      </BasePage>
    );
  }

  if (testsStatus !== "loaded") {
    return (
      <BasePage activeItem="results" contentClassName="pt-0 md:pt-0">
        <LoadingSpinner labelKey="results.loading_past_exams_alt" />
      </BasePage>
    );
  }

  if (!model) {
    return (
      <BasePage activeItem="results" contentClassName="pt-0 md:pt-0">
        <div className="flex min-h-full items-center justify-center bg-[#f4f4f6] p-6">
          <ResultsEmptyState
            buttonLabel={t("results.quick_practice_cta")}
            description={t("results.no_results_description")}
            footer={t("results.quick_practice_duration")}
            onAction={() => navigate("/study")}
            title={t("results.no_results_title")}
          />
        </div>
      </BasePage>
    );
  }

  if (needsToCreateProfile) {
    return (
      <BasePage activeItem="results" contentClassName="pt-0 md:pt-0">
        <div className="flex min-h-full items-center justify-center bg-[#f4f4f6] p-6">
          <ResultsEmptyState
            buttonLabel={t("results.complete_profile_cta")}
            description={t("results.complete_profile_description")}
            onAction={() => navigate("/create-profile?routeAfter=results")}
            title={t("results.complete_profile")}
          />
        </div>
      </BasePage>
    );
  }

  return (
    <BasePage activeItem="results" contentClassName="pt-0 md:pt-0">
      <ResultsDashboard model={model} />
    </BasePage>
  );
});

export default ExamResults;
